import React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { BOOT_TIMER_OPTIONS, type GamePlayerResponse } from '@warcarp/api-types'
import type { GameState } from '../../lib/gameState'
import { overdueBootTarget, shortBootDuration } from '../../lib/bootAffordance'
import { useNow } from '../../hooks/useNow'
import { getItem, setItem } from '../../lib/storage'
import { reportError } from '../../lib/reporting'

export function BootOverdueNotice({ gameId, state, players, viewerUserId, bootableAt, bootAfterSeconds, onBootPlayer }: {
  gameId: string
  state: GameState | null
  players: GamePlayerResponse[]
  viewerUserId?: string
  bootableAt?: string | null
  bootAfterSeconds: number
  onBootPlayer: (userId: string, name: string) => void
}) {
  const now = useNow(bootableAt ? 1000 : null)
  const target = overdueBootTarget({ state, players, viewerUserId, bootableAt, bootAfterSeconds, now })
  // Deadline changes when a new turn begins or vacation ends. Store dismissal
  // per effective turn so navigating away cannot revive the same notice.
  const key = target && bootableAt ? `boot_notice_${gameId}_${target.userId}_${Date.parse(bootableAt)}` : null
  const [checkedKey, setCheckedKey] = React.useState<string | null>(null)
  const [dismissedKey, setDismissedKey] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!key) return
    let active = true
    void getItem(key).then(value => {
      if (!active) return
      setDismissedKey(value === '1' ? key : null)
      setCheckedKey(key)
    }).catch(error => {
      reportError(error, { tags: { area: 'boot-notice-read' } })
      if (active) setCheckedKey(key)
    })
    return () => { active = false }
  }, [key])

  if (!target || !key || checkedKey !== key || dismissedKey === key) return null
  const heldFor = shortBootDuration(now - Date.parse(bootableAt!) + bootAfterSeconds * 1000)
  const timerLabel = BOOT_TIMER_OPTIONS.find(option => option.seconds === bootAfterSeconds)?.label ?? shortBootDuration(bootAfterSeconds * 1000)
  const dismiss = () => {
    setDismissedKey(key)
    void setItem(key, '1').catch(error => reportError(error, { tags: { area: 'boot-notice-dismiss' } }))
  }
  return (
    <View className="flex-row items-center border-y border-olive bg-paper-up px-2 py-1" style={{ minHeight: 42 }} testID="boot-overdue-notice">
      <TouchableOpacity onPress={() => onBootPlayer(target.userId, target.displayName)} accessibilityRole="button" accessibilityLabel={`Boot ${target.displayName}; turn held for ${heldFor}`} className="flex-1 flex-row items-center gap-2" style={{ minWidth: 0 }}>
        <MaterialCommunityIcons name="clock-alert-outline" size={16} color="#b4b999" />
        <View className="flex-1" style={{ minWidth: 0 }}>
          <Text className="font-typed text-ink text-xs" numberOfLines={1}><Text className="text-blood">{target.displayName}</Text> has held the turn <Text className="text-blood">{heldFor}</Text></Text>
          <Text className="font-typed text-ink-soft text-[10px]" numberOfLines={1}>This game&apos;s boot timer is {timerLabel}</Text>
        </View>
        <View className="rounded-full border border-blood bg-blood/10 px-3 py-1"><Text className="font-stencil text-blood text-xs">⏏ Boot</Text></View>
      </TouchableOpacity>
      <TouchableOpacity onPress={dismiss} accessibilityRole="button" accessibilityLabel="Dismiss boot notice" hitSlop={8} className="items-center justify-center" style={{ width: 32, height: 32 }}><Text className="font-stencil text-ink" style={{ fontSize: 18, lineHeight: 20 }}>×</Text></TouchableOpacity>
    </View>
  )
}
